import React, { useMemo } from 'react';
import { SimpleCohortTypeKey } from '../../../../../utils/apollo/graphqlSchema';
import PerformedCohort from './PerformedCohort';
import FunnelCohort from './FunnelCohort';
import RetentionCohort from './RetentionCohort';
import MeasureCohort from './MeasureCohort';
import ExpressionCohort from './ExpressionCohort';
import DndDroppableZone from '../../../../DndEntity/DndDroppableZone';
import { EntityDropzoneType } from '../../../../QuickAccessDictionary/DictionaryTabs/types';
import { isEmptyEventConditions } from '../../../../../utils/builder';
import {
  EntityDropzoneDataTypeEnum,
  IEventEntityDropzoneDataType,
  IMeasureEntityDropzoneDataType,
} from '../../../../DndEntity/validation';
import { useDndEntityInternalContext } from '../../../../DndEntity';
import {
  createDictionaryEntityDropzoneId,
  getDndActiveItemData,
} from '../../../../DndEntity/utils';
import { MAX_EVENT_CONDITIONS_COUNT } from '../../../../../config/constants';
import { useCohortRowContext } from '../CohortRowContext';
import { UnreachableCaseError } from 'ts-essentials';

const SimpleCohort = () => {
  const { rowMeta, cohortRow } = useCohortRowContext();
  const { simpleCohortTypeKey } = cohortRow;

  const { activeItem } = useDndEntityInternalContext();

  const dropzoneId = createDictionaryEntityDropzoneId(
    EntityDropzoneType.Measures,
    rowMeta,
    '-Cohort-Measure-Droppable',
  );

  const checkHasErrors = () => {
    const data = getDndActiveItemData(activeItem);

    const isTagLeafEvent =
      data?.__typename === 'TagLeafBasicEvent' ||
      data?.__typename === 'TagLeafVirtualEvent';

    return (
      isTagLeafEvent &&
      cohortRow.eventConditions.length >= MAX_EVENT_CONDITIONS_COUNT
    );
  };

  const getCustomMessage = () => {
    const data = getDndActiveItemData(activeItem);

    if (cohortRow.eventConditions.length >= MAX_EVENT_CONDITIONS_COUNT) {
      return `Limit of 5 Events reached`;
    }

    const isTagLeafEvent =
      data?.__typename === 'TagLeafBasicEvent' ||
      data?.__typename === 'TagLeafVirtualEvent';

    if (isTagLeafEvent) {
      return '+ Add Event';
    }
    return `Replace Measure`;
  };

  const dropzoneData = useMemo(() => {
    const data = getDndActiveItemData(activeItem);

    if (data) {
      if (
        data.__typename === 'TagLeafBasicEvent' ||
        data.__typename === 'TagLeafVirtualEvent'
      ) {
        const areEventConditionsEmpty = isEmptyEventConditions(
          cohortRow.eventConditions,
        );

        return {
          id: dropzoneId,
          rowMeta: {
            ...rowMeta,
            eventIndex: areEventConditionsEmpty
              ? 0
              : cohortRow.eventConditions.length,
          },
          isNewEvent: !areEventConditionsEmpty,
          shouldAddMeasureFunction: !cohortRow?.fn?.key,
          type: EntityDropzoneDataTypeEnum.Events,
        } satisfies IEventEntityDropzoneDataType;
      } else {
        return {
          type: EntityDropzoneDataTypeEnum.Measures,
          rowMeta,
        } satisfies IMeasureEntityDropzoneDataType;
      }
    }
  }, [activeItem, rowMeta, cohortRow, dropzoneId]);

  switch (simpleCohortTypeKey) {
    case SimpleCohortTypeKey.Performed: {
      return <PerformedCohort />;
    }
    case SimpleCohortTypeKey.Expression: {
      return <ExpressionCohort />;
    }
    case SimpleCohortTypeKey.Measure: {
      return (
        <DndDroppableZone
          accepts={[EntityDropzoneType.Measures, EntityDropzoneType.Events]}
          id={dropzoneId}
          data={dropzoneData}
          customMessage={getCustomMessage()}
          hasError={checkHasErrors()}
        >
          <MeasureCohort cohortRow={cohortRow} rowMeta={rowMeta} />
        </DndDroppableZone>
      );
    }
    case SimpleCohortTypeKey.Retention: {
      return <RetentionCohort />;
    }
    case SimpleCohortTypeKey.Funnel: {
      return <FunnelCohort cohortRow={cohortRow} rowMeta={rowMeta} />;
    }
    default: {
      throw new UnreachableCaseError(simpleCohortTypeKey);
    }
  }
};

export default SimpleCohort;
